/* eslint-disable indent */
import MailboxTableHeader from '@src/main/modules/messages/ui/mailbox/components/mailbox/table/MailboxTableHeader';
import { usePunctualityReports } from '@src/main/modules/reports/punctuality/application/hook/usePunctualityReports';
import {
  emptyPunctualityReportCriteria,
  type PunctualityReport,
} from '@src/main/modules/reports/shared/domain/report.interface';
import {
  getAircraftRegistrationColumnProps,
  getArrivalAirportColumnProps,
  getCarrierCodeColumnProps,
  getDepartureAirportColumnProps,
  getEmployeeIdColumnProps,
  getFlightDateColumnProps,
  getFlightNumberColumnProps,
  getUpdatedAtColumnProps,
} from '@src/main/modules/reports/ui/components/table/ReportsColumns';
import ReportsTable from '@src/main/modules/reports/ui/components/table/ReportsTable';
import { usePunctualityColumnBodies } from '@src/main/modules/reports/ui/hooks/usePunctualityColumnBodies';
import { usePunctualityDateFilter } from '@src/main/modules/reports/ui/hooks/usePunctualityDateFilter';
import { usePunctualityExport } from '@src/main/modules/reports/ui/hooks/usePunctualityExport';
import { usePunctualityFilterHandler } from '@src/main/modules/reports/ui/hooks/usePunctualityFilterHandler';
import { usePunctualityFilters } from '@src/main/modules/reports/ui/hooks/usePunctualityFilters';
import { usePunctualityActions } from '@src/main/modules/reports/ui/hooks/usePunctualityReportActions';
import { mapPunctualityFiltersToCriteria } from '@src/main/modules/reports/ui/mapPunctualityFiltersToCriteria';
import InvalidDateRangeDialog from '@src/main/shared/ui/components/calendar/InvalidDateRangeDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { getThreeMonthDateRange } from '@src/main/shared/utils/dateUtils';
import { Column } from 'primereact/column';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PunctualityView.module.scss';

type PunctualityViewProps = {
  formType: string;
};

const PunctualityView: FC<PunctualityViewProps> = ({ formType }) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useMemo(() => getThreeMonthDateRange(), []);
  const initialFilters = usePunctualityFilters(startDate, endDate);

  const [invalidDateModalVisible, setInvalidDateModalVisible] = useState(false);

  const reportsHook = usePunctualityReports(initialFilters, mapPunctualityFiltersToCriteria, {
    ...emptyPunctualityReportCriteria,
    formType,
    sortField: 'flightDate',
    sortOrder: 'desc',
  });

  const {
    filters,
    setFilters,
    reports,
    total,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    refetchReports,
    handleSort,
    sortField,
    sortOrder,
  } = reportsHook;

  const {
    downloadPdf,
    handleDeleteReport,
    confirmDeleteReport,
    rejectDeleteReport,
    reportToDelete,
    isDeleteModalVisible,
  } = usePunctualityActions();

  const { handleSetFilters } = usePunctualityFilterHandler(
    setFilters,
    refetchReports,
    setInvalidDateModalVisible,
  );

  const { pdfColumnBody, deleteColumnBody } = usePunctualityColumnBodies(
    t,
    downloadPdf,
    handleDeleteReport,
  );

  const detailedCriteria = useMemo(
    () => mapPunctualityFiltersToCriteria(filters, { ...emptyPunctualityReportCriteria, formType }),
    [filters, formType],
  );

  const { onExportExcel } = usePunctualityExport(detailedCriteria, formType, 'informe_puntualidad');

  const flightDateFilterTemplate = usePunctualityDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const updatedAtFilterTemplate = usePunctualityDateFilter({
    t,
    setInvalidDateModalVisible,
  });

  const header = (
    <MailboxTableHeader
      onRefresh={refetchReports}
      onClearFilters={() => {
        setFilters(initialFilters);
        refetchReports();
      }}
      onExportExcel={onExportExcel}
      showExportButtons
    />
  );

  return (
    <div className={styles.container}>
      <AppLoading isLoading={isLoading || isFetching}>
        <ReportsTable<PunctualityReport>
          data={reports}
          filters={filters}
          setFilters={handleSetFilters}
          header={header}
          loading={isLoading}
          dataKey="id"
          globalFilterFields={[
            'carrierCode',
            'flightNumber',
            'departureAirport',
            'arrivalAirport',
            'aircraftRegistration',
            'employeeId',
          ]}
          totalRecords={total}
          first={pageNumber * pageSize}
          rows={pageSize}
          onPage={handlePageChange}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
        >
          <Column {...getFlightDateColumnProps(t, flightDateFilterTemplate)} />
          <Column {...getCarrierCodeColumnProps(t)} />
          <Column {...getFlightNumberColumnProps(t)} />
          <Column {...getDepartureAirportColumnProps(t)} />
          <Column {...getArrivalAirportColumnProps(t)} />
          <Column {...getAircraftRegistrationColumnProps(t)} />
          <Column {...getEmployeeIdColumnProps(t)} />
          <Column {...getUpdatedAtColumnProps(t, updatedAtFilterTemplate)} />
          <Column body={pdfColumnBody} header={t('common.pdf')} />
          <Column body={deleteColumnBody} header={t('common.delete')} />
        </ReportsTable>

        <InvalidDateRangeDialog
          invalidDateRangeModalVisible={invalidDateModalVisible}
          setInvalidDateRangeModalVisible={setInvalidDateModalVisible}
          t={t}
        />

        <ConfirmDeleteDialog
          confirmDelete={() => confirmDeleteReport(refetchReports)}
          rejectDelete={rejectDeleteReport}
          visible={isDeleteModalVisible}
          message={t('common.message.confirmDelete', {
            flight: reportToDelete?.flightNumber,
            carrier: reportToDelete?.carrierCode,
          })}
        />
      </AppLoading>
    </div>
  );
};

export default PunctualityView;
